/**
 * SEO 工具函数
 * 用于动态设置页面的 meta 标签
 */

/**
 * 设置页面 SEO 信息
 * @param {Object} options SEO 配置对象
 * @param {string} options.title 页面标题
 * @param {string} options.description 页面描述
 * @param {string} options.keywords 页面关键词
 * @param {string} options.image 页面图片（用于社交分享）
 * @param {string} options.url 页面URL
 * @param {string} options.type 页面类型（website/article等）
 */
export function setSEO(options = {}) {
  const {
    title = '航空贵宾厅预约系统',
    description = '珠海启程商旅服务有限公司提供专业的航空贵宾厅预约服务，为您提供舒适的候机体验。航空贵宾预约，选择启程商旅。',
    keywords = '珠海启程商旅服务有限公司,启程商旅,航空贵宾预约,航空贵宾厅预约,机场VIP,贵宾厅预约,航空贵宾厅',
    image = 'https://www.qchsvip.cn/logo.png',
    url = window.location.href,
    type = 'website'
  } = options

  const siteName = '珠海启程商旅服务有限公司 - 航空贵宾厅预约系统'
  const fullTitle = title.includes('珠海启程商旅服务有限公司') || title.includes(siteName) ? title : `${title} - ${siteName}`

  // 设置基础 meta 标签
  setMetaTag('title', fullTitle)
  setMetaTag('description', description)
  setMetaTag('keywords', keywords)

  // 设置 Open Graph 标签
  setMetaTag('og:title', fullTitle, 'property')
  setMetaTag('og:description', description, 'property')
  setMetaTag('og:image', image, 'property')
  setMetaTag('og:url', url, 'property')
  setMetaTag('og:type', type, 'property')
  setMetaTag('og:site_name', siteName, 'property')

  // 设置 Twitter Card 标签
  setMetaTag('twitter:card', 'summary_large_image')
  setMetaTag('twitter:title', fullTitle)
  setMetaTag('twitter:description', description)
  setMetaTag('twitter:image', image)

  // 设置 canonical 链接
  setCanonical(url)

  // 设置页面标题
  document.title = fullTitle
}

/**
 * 设置或更新 meta 标签
 * @param {string} name meta 标签的 name 或 property
 * @param {string} content 内容
 * @param {string} attr 属性类型：'name' 或 'property'
 */
function setMetaTag(name, content, attr = 'name') {
  if (!content) return

  let meta = document.querySelector(`meta[${attr}="${name}"]`)
  
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attr, name)
    document.head.appendChild(meta)
  }
  
  meta.setAttribute('content', content)
}

/**
 * 设置 canonical 链接
 * @param {string} url 规范URL
 */
function setCanonical(url) {
  if (!url) return

  let link = document.querySelector('link[rel="canonical"]')
  
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    document.head.appendChild(link)
  }
  
  link.setAttribute('href', url)
}

/**
 * 添加结构化数据（JSON-LD）
 * @param {Object} data 结构化数据对象
 */
export function addStructuredData(data) {
  // 移除旧的脚本
  const oldScript = document.querySelector('script[type="application/ld+json"]')
  if (oldScript) {
    oldScript.remove()
  }

  // 创建新的脚本
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.text = JSON.stringify(data)
  document.head.appendChild(script)
}

/**
 * 生成组织结构化数据
 */
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '珠海启程商旅服务有限公司',
    alternateName: '启程商旅',
    url: 'https://www.qchsvip.cn',
    logo: 'https://www.qchsvip.cn/logo.png',
    description: '珠海启程商旅服务有限公司提供专业的航空贵宾厅预约服务，航空贵宾预约，选择启程商旅。',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '珠海',
      addressCountry: 'CN'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese']
    },
    sameAs: []
  }
}

/**
 * 生成产品/服务结构化数据
 * @param {Object} product 产品信息
 */
export function generateProductSchema(product) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name || '航空贵宾厅服务',
    description: product.description || '',
    image: product.image || '',
    offers: {
      '@type': 'Offer',
      price: product.price || '0',
      priceCurrency: 'CNY',
      availability: 'https://schema.org/InStock'
    }
  }
}

/**
 * 生成面包屑导航结构化数据
 * @param {Array} items 面包屑项 [{name, url}]
 */
export function generateBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  }
}
